import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { BehaviorSubject, map } from 'rxjs';

import { StreamData } from '../shared/stream.token';
import { VideoLayout, VideoLayoutService } from '../shared/video-layout.service';

import { RaceSessionService } from '../timing-tower/race-session.service';
import { TimingTowerService } from '../timing-tower/timing-tower.service';
import { VbLxpVideoPlayerComponent } from '../video/video-player/vb-lxp-video-player.component';
import { MOBILE_SIZE, WindowSizeService } from '../shared/window-size.service';
import { WebSocketService } from '../shared/websocket.service';

export type SlotItem =
	| { type: 'timing'; id: string; streamData: StreamData; timestamp?: number }
	| { type: 'mainStream' }
	| { type: 'twitchStream' };

@Injectable({
	providedIn: 'root',
})
export class LivestreamViewService {
	private destroyRef = inject(DestroyRef);
	public timingTowerService = inject(TimingTowerService);
	public layoutService = inject(VideoLayoutService);
	public raceSessionService = inject(RaceSessionService);
	private windowSizeService = inject(WindowSizeService);
	private webSocketService = inject(WebSocketService);

	public isMainStreamSelected = signal(true);
	public isTwitchStreamSelected = signal(false);

	private mainStreamsData: StreamData[] = [];
	public selectedMainStreamId = signal<string | null>(null);
	public raceStreamData = [];
	public slotStreamData = [];
	public currentPlayers: VbLxpVideoPlayerComponent[] = [];

	public getTwitchStreamData(): StreamData | null {
		const twitchFromMain = this.mainStreamsData.find((s) => s.id === 'main-stream-twitch');

		if (!twitchFromMain?.hlsPlaybackUrl) {
			return null;
		}

		return {
			...twitchFromMain,
			id: 'main-stream-twitch',
			hlsPlaybackUrl: twitchFromMain.hlsPlaybackUrl,
			isLive: twitchFromMain.isLive ?? true,
		};
	}
	public occupiedSlots$ = new BehaviorSubject<SlotItem[]>([]);
	public isNoPlayerSelected = signal(true);
	public slotStreamData$ = this.occupiedSlots$.pipe(
		map((slots) => {
			return slots.map((s) => {
				switch (s?.type) {
					case 'timing':
						return this.raceStreamData.find((rsd) => rsd.carNumber === s.id);
					case 'mainStream':
						return this.getMainStreamData();
					case 'twitchStream':
						return this.getTwitchStreamData();
				}
			});
		}),
	);

	constructor() {
		// Subscribe to main-stream updates from RaceSessionService
		this.raceSessionService.mainStream$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((streams) => {
				this.mainStreamsData = Array.isArray(streams) ? streams : streams ? [streams] : [];
				if (!this.selectedMainStreamId() && this.mainStreamsData.length > 0) {
					this.selectedMainStreamId.set(this.mainStreamsData[0].id);
				}
				this.ensureMainStreamSlotIfReady();
				this.refreshMainStreamPlayersIfPresent();
			});

		this.raceSessionService.raceStreamData$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((raceStreamData) => {
				this.raceStreamData = raceStreamData;
			});

		this.slotStreamData$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((streamData) => {
			this.slotStreamData = streamData;
		});

		// Check if slots overflow max selectable per layout
		this.occupiedSlots$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((slots: SlotItem[]) => {
			for (let i = 0; i < this.currentPlayers.length; i++) {
				const currentPlayer = this.currentPlayers[i];
				const currentSlot = this.occupiedSlots$.value[i];
				if (currentSlot === undefined) {
					currentPlayer.setStreamData(undefined);
				} else if (
					currentSlot?.type === 'mainStream' &&
					!currentPlayer.getPlayerStreamId()?.includes('main-stream')
				) {
					currentPlayer.setStreamData(this.getMainStreamData());
				} else if (
					currentSlot?.type === 'twitchStream' &&
					!currentPlayer.getPlayerStreamId()?.includes('twitch')
				) {
					currentPlayer.setStreamData(this.getTwitchStreamData());
				} else if (
					currentSlot?.type === 'timing' &&
					currentPlayer.getPlayerStreamId() !== currentSlot.id
				) {
					currentPlayer.setStreamData(currentSlot.streamData);
				}
			}

			this.handleSelectionUpdate(
				slots,
				slots.length >
					this.layoutService.getLayoutPlayerCount(this.layoutService.activeLayout$.value),
			);
			this.isNoPlayerSelected.set(slots.length === 0);
		});

		this.layoutService.activeLayout$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((layout: VideoLayout) => {
				if (
					this.occupiedSlots$.value.filter((s) => s !== undefined).length <=
					this.layoutService.getLayoutPlayerCount(layout)
				) {
					return;
				}
				this.pruneOverflow(layout);
			});
	}

	/*
	Prune the occupiedSlots$ array to fit within the capacity of the given layout.
	Evict the oldest items timing and replace them with the newest items.
	If mainStream or Twitch Stream is selected, it will always be preserved and not evicted.
	*/
	public pruneOverflow(layout: VideoLayout) {
		const isMobile = this.windowSizeService.windowSize().width < MOBILE_SIZE;
		const slots = isMobile
			? this.occupiedSlots$.value.filter((s) => s !== undefined)
			: this.occupiedSlots$.value.filter((s) => s !== undefined && s.type !== 'mainStream');

		const sortedSlots = [...slots];
		if (!isMobile) {
			sortedSlots.sort((a, b) => {
				if (a.type === 'timing' && b.type === 'timing') {
					return (b.timestamp ?? 0) - (a.timestamp ?? 0);
				} else if (a.type === 'twitchStream' && b.type === 'timing') {
					return -1;
				} else if (a.type === 'timing' && b.type === 'twitchStream') {
					return 1;
				} else {
					return 0;
				}
			});
		} else {
			sortedSlots.reverse();
		}

		const capacity =
			this.layoutService.getLayoutPlayerCount(layout) -
			(this.isMainStreamSelected() && !isMobile ? 1 : 0);

		const overflow = slots.length - capacity;
		if (overflow <= 0) return;

		// oldest items whose positions will be reused (reversed so oldest-first)
		const toEvict = sortedSlots.slice(capacity).reverse();
		// newest items that will fill those positions (newest-first)
		const newestItems = sortedSlots.slice(0, overflow);

		// Track original indices of newest items — these will be vacated
		const indicesToRemove = new Set<number>(newestItems.map((item) => slots.indexOf(item)));

		const result = [...slots];
		// Replace each evicted slot's position with a newest item in-place
		toEvict.forEach((evictedItem, i) => {
			const idx = result.indexOf(evictedItem);
			result[idx] = newestItems[i];
		});

		// Remove the now-vacated original positions of the newest items
		const newSlots: SlotItem[] = [];
		if (this.isMainStreamSelected() && !isMobile) {
			newSlots.push({ type: 'mainStream' });
		}
		newSlots.push(...result.filter((_, i) => !indicesToRemove.has(i)));

		this.occupiedSlots$.next(newSlots);

		for (const evictedSlot of toEvict) {
			if (evictedSlot.type === 'timing') {
				this.timingTowerService.setInactive(evictedSlot.id);
			} else if (evictedSlot.type === 'twitchStream') {
				this.isTwitchStreamSelected.set(false);
			} else if (evictedSlot.type === 'mainStream') {
				this.isMainStreamSelected.set(false);
			}
		}
	}

	public addSlotItem(slotItem: SlotItem) {
		const currentSlots = this.occupiedSlots$.value;
		if (!currentSlots.includes(undefined)) {
			this.occupiedSlots$.next([...currentSlots, slotItem]);
			return;
		}
		let added = false;
		const slots = this.occupiedSlots$.value.map((s) => {
			if (s === undefined && !added) {
				added = true;
				return slotItem;
			} else {
				return s;
			}
		});
		this.occupiedSlots$.next(slots);
	}

	public removeSlotItem(slotItem: SlotItem) {
		const currentSlots = this.occupiedSlots$.value;
		if (slotItem.type === 'timing') {
			let removed = false;
			const slots = currentSlots.map((s) => {
				if (
					s?.type === 'timing' &&
					s?.streamData.carNumber === slotItem.streamData.carNumber &&
					!removed
				) {
					removed = true;
					return undefined;
				} else {
					return s;
				}
			});
			this.occupiedSlots$.next(slots);
		} else {
			const slots = currentSlots.map((s) => {
				if (s?.type === slotItem.type) {
					return undefined;
				} else {
					return s;
				}
			});
			this.occupiedSlots$.next(slots);
		}
	}

	public updateCurrentPlayers(players: VbLxpVideoPlayerComponent[]) {
		this.currentPlayers = players;
		for (let i = 0; i < this.occupiedSlots$.value.length; i++) {
			const currentSlot = this.occupiedSlots$.value[i];
			let streamData: StreamData;
			if (currentSlot.type === 'timing') {
				if (this.raceStreamData.length) {
					streamData = this.raceStreamData.find((rsd) => rsd.carNumber === currentSlot.id);
				} else {
					streamData = currentSlot.streamData;
				}
			} else if (currentSlot.type === 'mainStream') {
				streamData = this.getMainStreamData();
			} else if (currentSlot.type === 'twitchStream') {
				streamData = this.getTwitchStreamData();
			}
			const currentPlayer = this.currentPlayers[i];
			if (
				(currentSlot?.type === 'mainStream' &&
					currentPlayer.getPlayerStreamId()?.includes('mainStream')) ||
				(currentSlot?.type === 'twitchStream' &&
					currentPlayer.getPlayerStreamId()?.includes('twitch')) ||
				(currentSlot?.type === 'timing' && currentPlayer.getPlayerStreamId() === currentSlot.id)
			) {
				continue;
			}
			currentPlayer.setStreamData(streamData);
		}
	}

	public handleSelectionUpdate(slots: SlotItem[], isAdd: boolean) {
		if (!isAdd) {
			return;
		}

		if (this.windowSizeService.windowSize().width < MOBILE_SIZE) {
			if (slots.length > 1) {
				this.pruneOverflow(this.layoutService.activeLayout$.value);
			}
			return;
		}

		switch (slots.filter((s) => s !== undefined).length) {
			case 1:
				this.layoutService.setLayout(VideoLayout.SINGLE);
				break;
			case 2:
				this.layoutService.setLayout(VideoLayout.STACKED);
				break;
			case 3:
				this.layoutService.setLayout(VideoLayout.LARGE_LEFT);
				break;
			case 4:
				this.layoutService.setLayout(VideoLayout.GRID_2X2);
				break;
			default:
				if (slots.length > 4) {
					this.pruneOverflow(this.layoutService.activeLayout$.value);
				}
				break;
		}
	}

	public get mainStreams(): StreamData[] {
		return this.mainStreamsData;
	}

	public selectMainStream(id: string): void {
		this.selectedMainStreamId.set(id);
		this.refreshMainStreamPlayersIfPresent();
	}

	public getMainStreamData(): StreamData | null {
		if (this.mainStreamsData.length === 0) return null;
		const selected = this.selectedMainStreamId();
		return this.mainStreamsData.find((s) => s.id === selected) ?? this.mainStreamsData[0];
	}

	public getStreamDataByCarNumber(carNumber: string): StreamData {
		return this.raceStreamData.find((sd) => sd.carNumber === carNumber);
	}

	private ensureMainStreamSlotIfReady(): void {
		if (
			(this.getMainStreamData() &&
				this.isMainStreamSelected() &&
				this.occupiedSlots$.value.includes(undefined)) ||
			!this.occupiedSlots$.value.some((slot) => slot?.type === 'mainStream')
		) {
			this.addSlotItem({ type: 'mainStream' });
		}
	}

	private ensureTwitchStreamSlotIfReady(): void {
		if (
			this.getTwitchStreamData() &&
			this.isTwitchStreamSelected() &&
			!this.occupiedSlots$.value.some((slot) => slot?.type === 'twitchStream')
		) {
			this.addSlotItem({ type: 'twitchStream' });
		}
	}

	private refreshMainStreamPlayersIfPresent(): void {
		if (!this.getMainStreamData()) {
			return;
		}

		const slots = this.occupiedSlots$.value;
		for (let i = 0; i < slots.length; i++) {
			if (slots[i].type !== 'mainStream') {
				continue;
			}

			const player = this.currentPlayers[i];
			if (player) {
				player.setStreamData(this.getMainStreamData());
			}
		}
	}

	public onMainStreamToggled(isSelected: boolean) {
		if (isSelected) {
			this.isMainStreamSelected.set(true);
			this.ensureMainStreamSlotIfReady();
		} else {
			this.removeSlotItem({ type: 'mainStream' });
			this.isMainStreamSelected.set(false);
		}
	}

	public onTwitchStreamToggled(isSelected: boolean) {
		if (isSelected) {
			this.isTwitchStreamSelected.set(true);
			this.ensureTwitchStreamSlotIfReady();
		} else {
			this.removeSlotItem({ type: 'twitchStream' });
			this.isTwitchStreamSelected.set(false);
		}
	}
}
